import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { apiService } from '@/services/api'
import { isClientSideNetworkError } from '@/components/NetworkTroubleshootingModal'
import { getStoredAttribution, clearStoredAttribution } from '@/utils/attribution'

/**
 * True when the person is signing up from a team invitation: on the join
 * page, where the invitation's own sign-up form lives, or while an
 * invitation is still waiting to be accepted. The server gives everybody a
 * team of one at init and the invitation is accepted a moment later, so it
 * cannot tell on its own; told, it does not welcome them as a new account.
 *
 * The answer is kept for the tab. Init is retried, and by the time a retry
 * goes out the person may have left the join page and the invitation may
 * have been accepted and its token removed. Without the marker that retry
 * would say nothing and the server would welcome them after all.
 */
export function arrivedOnInvitation(): boolean {
  try {
    if (sessionStorage.getItem('arrivedOnTeamInvite')) return true
    const arrived = window.location.pathname.startsWith('/team/join/') || Boolean(sessionStorage.getItem('pendingTeamInvite'))
    if (arrived) sessionStorage.setItem('arrivedOnTeamInvite', '1')
    return arrived
  } catch {
    return false
  }
}

interface UserInitContextType {
  /** Whether the user has been successfully initialized in Supabase */
  isInitialized: boolean
  /** Whether initialization is currently in progress */
  isInitializing: boolean
  /** Any error that occurred during initialization */
  initError: Error | null
  /** Whether we've detected a client-side network connectivity issue */
  hasNetworkIssue: boolean
  /** Dismiss the network issue state (user acknowledged or wants to try again) */
  dismissNetworkIssue: () => void
  /** Retry initialization manually (e.g., after recovering from an error) */
  retryInit: () => void
}

const UserInitContext = createContext<UserInitContextType | undefined>(undefined)

// Constants for retry logic
const MAX_AUTO_RETRIES = 3
const INITIAL_RETRY_DELAY_MS = 1000 // 1 second
const MAX_RETRY_DELAY_MS = 8000 // 8 seconds

/**
 * Check if an error is retryable (transient errors that may recover)
 */
function isRetryableError(error: any): boolean {
  // Network errors
  if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
    return true
  }

  // HTTP status codes that indicate transient issues
  const status = error?.status || error?.response?.status
  if (status === 503 || status === 502 || status === 504 || status === 429) {
    return true
  }

  // Server explicitly says it's a transient error
  if (error?.message?.includes('temporarily unavailable') ||
      error?.message?.includes('maintenance') ||
      error?.message?.includes('try again')) {
    return true
  }

  return false
}

/**
 * Check if an error is non-retryable (permanent failures)
 */
function isNonRetryableError(error: any): boolean {
  const status = error?.status || error?.response?.status
  // 401: Auth error, 409: Conflict (email exists), 400: Bad request
  return status === 401 || status === 409 || status === 400
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function UserInitProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth()
  const { user, isLoaded } = useUser()

  const [isInitialized, setIsInitialized] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [initError, setInitError] = useState<Error | null>(null)
  const [hasNetworkIssue, setHasNetworkIssue] = useState(false)

  // Track if we've attempted initialization to prevent duplicates
  const hasAttemptedRef = useRef(false)
  // Track retry count for auto-retry
  const retryCountRef = useRef(0)
  // Track consecutive network errors to detect client-side connectivity issues
  const networkErrorCountRef = useRef(0)

  const initializeUser = async (isAutoRetry = false) => {
    // Guard: only initialize once per session - don't retry automatically
    // unless this is an auto-retry for transient errors
    if (hasAttemptedRef.current && !isAutoRetry) {
      return
    }

    // Guard: need to be signed in with a valid email
    if (!isSignedIn || !user?.primaryEmailAddress?.emailAddress) {
      return
    }

    hasAttemptedRef.current = true
    setIsInitializing(true)
    setInitError(null)

    try {
      console.log('🎉 [UserInitContext] Initializing user account...', {
        email: user.primaryEmailAddress.emailAddress,
        userId: user.id,
        attempt: retryCountRef.current + 1
      })

      await apiService.initializeUser({
        email: user.primaryEmailAddress.emailAddress,
        firstName: user.firstName || undefined,
        lastName: user.lastName || undefined,
        attribution: getStoredAttribution() || undefined,
        invited: arrivedOnInvitation() || undefined
      })

      // Attribution has been handed to the server; stop resending it on future inits
      clearStoredAttribution()

      console.log('✅ [UserInitContext] User account initialized successfully!')
      setIsInitialized(true)
      retryCountRef.current = 0 // Reset retry count on success
    } catch (error: any) {
      console.error('❌ [UserInitContext] Failed to initialize user:', error)

      // Track if this is a client-side network error
      if (isClientSideNetworkError(error)) {
        networkErrorCountRef.current++
        console.warn(`⚠️ [UserInitContext] Network error detected (count: ${networkErrorCountRef.current})`)
      }

      // Check if we should auto-retry for transient errors
      if (isRetryableError(error) && retryCountRef.current < MAX_AUTO_RETRIES) {
        retryCountRef.current++
        const delayMs = Math.min(
          INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current - 1),
          MAX_RETRY_DELAY_MS
        )
        console.log(`🔄 [UserInitContext] Transient error, auto-retrying in ${delayMs}ms (attempt ${retryCountRef.current}/${MAX_AUTO_RETRIES})...`)

        setIsInitializing(false) // Temporarily show not initializing
        await sleep(delayMs)

        // Retry
        return initializeUser(true)
      }

      // If init fails, check if user actually exists AND is fully initialized
      // This handles edge cases like email conflicts or race conditions
      try {
        console.log('🔍 [UserInitContext] Checking if user exists despite init failure...')
        const profileResponse = await apiService.getProfile()
        if (profileResponse?.data) {
          const profile = profileResponse.data
          // Only mark as initialized if user has essential data:
          // - activeTeamId: Required for scans, schemas, and most features
          //
          // Older/free accounts may have a healthy workspace but no credit
          // history. Team membership is the source of truth for app readiness,
          // so a valid activeTeamId is enough to recover from init conflicts.
          const hasTeam = !!profile.activeTeamId
          const hasCreditsHistory = (profile.creditBalance ?? 0) > 0 || (profile.totalCreditsUsed ?? 0) > 0
          // Belt-and-suspenders: if the user has actually used the app
          // (i.e. consumed credits), they're clearly initialized. This protects
          // against future regressions where the API stops returning a field
          // we depend on, so a transient init failure can't lock real users out.
          const hasUsedApp = (profile.totalCreditsUsed ?? 0) > 0

          if (hasTeam || hasUsedApp) {
            console.log('✅ [UserInitContext] User fully initialized, marking as initialized')
            setIsInitialized(true)
            setInitError(null)
            retryCountRef.current = 0
            return
          } else {
            // User exists but is missing essential data - init failed silently
            // This can happen if Clerk webhook created user but init endpoint failed
            console.warn('⚠️ [UserInitContext] User exists but missing essential data:', {
              hasTeam,
              hasCreditsHistory,
              activeTeamId: profile.activeTeamId,
              creditBalance: profile.creditBalance,
              totalCreditsUsed: profile.totalCreditsUsed
            })

            // If we haven't exhausted auto-retries, try again
            // This handles the case where user exists but team/credits weren't created
            if (retryCountRef.current < MAX_AUTO_RETRIES && !isNonRetryableError(error)) {
              retryCountRef.current++
              const delayMs = Math.min(
                INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current - 1),
                MAX_RETRY_DELAY_MS
              )
              console.log(`🔄 [UserInitContext] User incomplete, auto-retrying init in ${delayMs}ms (attempt ${retryCountRef.current}/${MAX_AUTO_RETRIES})...`)

              setIsInitializing(false)
              await sleep(delayMs)

              return initializeUser(true)
            }

            // Exhausted retries - show error
            setInitError(new Error('Account setup incomplete. Please try again or contact support.'))
          }
        }
      } catch (profileError: any) {
        // Profile fetch failed too - user truly doesn't exist
        console.error('❌ [UserInitContext] Profile check also failed:', profileError)

        // Track if this is also a network error
        if (isClientSideNetworkError(profileError)) {
          networkErrorCountRef.current++
          console.warn(`⚠️ [UserInitContext] Second network error detected (count: ${networkErrorCountRef.current})`)
        }

        // If we have 2+ consecutive network errors, this is likely a client-side issue
        // Show the troubleshooting modal instead of a generic error
        if (networkErrorCountRef.current >= 2) {
          console.error('🔌 [UserInitContext] Multiple network errors detected - likely client-side connectivity issue')
          setHasNetworkIssue(true)
        }
      }

      setInitError(error instanceof Error ? error : new Error(error?.message || 'Unknown error'))
      retryCountRef.current = 0 // Reset for manual retry
    } finally {
      setIsInitializing(false)
    }
  }

  // Reset state when user signs out
  useEffect(() => {
    if (!isSignedIn && isLoaded) {
      setIsInitialized(false)
      setIsInitializing(false)
      setInitError(null)
      setHasNetworkIssue(false)
      hasAttemptedRef.current = false
      retryCountRef.current = 0
      networkErrorCountRef.current = 0
    }
  }, [isSignedIn, isLoaded])

  // Auto-initialize when user signs in
  useEffect(() => {
    if (isLoaded && isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      initializeUser()
    }
  }, [isLoaded, isSignedIn, user?.primaryEmailAddress?.emailAddress])

  const retryInit = () => {
    hasAttemptedRef.current = false
    retryCountRef.current = 0
    networkErrorCountRef.current = 0
    setInitError(null)
    setHasNetworkIssue(false)
    initializeUser()
  }

  const dismissNetworkIssue = () => {
    setHasNetworkIssue(false)
  }

  return (
    <UserInitContext.Provider value={{
      isInitialized,
      isInitializing,
      initError,
      hasNetworkIssue,
      dismissNetworkIssue,
      retryInit
    }}>
      {children}
    </UserInitContext.Provider>
  )
}

/**
 * Hook to access user initialization state.
 * Use this to gate API calls that require the user to exist in Supabase.
 *
 * @example
 * const { isInitialized } = useUserInit()
 *
 * const { data } = useQuery({
 *   queryKey: ['user-profile'],
 *   queryFn: () => apiService.getProfile(),
 *   enabled: isLoaded && isSignedIn && isInitialized, // Wait for init
 * })
 */
export function useUserInit() {
  const context = useContext(UserInitContext)
  if (context === undefined) {
    throw new Error('useUserInit must be used within a UserInitProvider')
  }
  return context
}
